import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	batchCreateQuoteRows,
	batchDeleteQuoteRows,
	batchUpdateQuoteRows,
	copyQuoteTable,
	createQuoteColumn,
	createQuoteRow,
	createQuoteTable,
	deleteQuoteColumn,
	deleteQuoteRow,
	deleteQuoteTable,
	getExistingValueColumns,
	getQuoteTables,
	reorderQuoteColumns,
	reorderQuoteRows,
	reorderQuoteTables,
	updateQuoteColumn,
	updateQuoteRow,
	updateQuoteTable,
	saveQuoteAsTemplate,
	createQuoteTableFromTemplate,
	updateQuoteSettings,
	syncQuoteTableLayout,
	batchUpdateCells,
	updateTabStatus,
} from "../../api/procurer-quotes-api";
import type { QuoteSettings, SyncLayoutPayload, CellUpdatePayload, TabStatusPayload, QuoteResourceType, BatchCreateRowPayload } from "../../api/procurer-quotes-api";
import {
	createSummaryTable,
	deleteSummaryTable,
	getSummaryTableData,
	getSummaryTables,
	updateSummaryTable,
} from "../../api/summary-tables-api";
import type {
	CreateSummaryTablePayload,
	UpdateSummaryTablePayload,
} from "../../api/summary-tables-api";
import type {
	BatchUpdateRowPayload,
	CreateColumnPayload,
	CreateQuoteRowPayload,
	CreateQuoteTablePayload,
	UpdateColumnPayload,
	UpdateQuoteRowPayload,
	UpdateQuoteTablePayload,
} from "../../api/procurer-quotes-api";
import type {
	RfxQuoteColumnType,
	RfxQuoteTable,
	RfxQuoteItem,
	SummaryTableConfig,
} from "../../Submission/types/quote.type";
import { showToast } from "utils/helpers";

const QUOTE_TABLES_KEY = "procurer-quote-tables";

export const useProcurerQuoteTables = (rfxId: number | null, resourceType: QuoteResourceType = "rfx") => {
	return useQuery({
		queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
		queryFn: () => getQuoteTables(rfxId!, resourceType),
		enabled: !!rfxId,
	});
};

export const useCreateQuoteTable = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: CreateQuoteTablePayload) =>
			createQuoteTable(rfxId, payload, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to create table. Please try again.", "Error");
		},
	});
};

export const useUpdateQuoteTable = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();
	const queryKey = [QUOTE_TABLES_KEY, resourceType, rfxId];

	return useMutation({
		mutationFn: (params: { payload: UpdateQuoteTablePayload; quoteId: number; }) =>
			updateQuoteTable(rfxId, params.quoteId, params.payload, resourceType),
		onMutate: async (params: { payload: UpdateQuoteTablePayload; quoteId: number; }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<RfxQuoteTable[]>(queryKey);
			if (previous) {
				const updated = previous.map((table) => {
					if (table.id !== params.quoteId) {
						return table;
					}
					const merged = { ...table, ...params.payload };
					// Map snake_case API field to camelCase cache field
					if (params.payload.include_totals_row !== undefined) {
						merged.includeTotalsRow = params.payload.include_totals_row;
					}
					return merged;
				});
				queryClient.setQueryData(queryKey, updated);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
			showToast("Failed to update table. Please try again.", "Error");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
};

export const useDeleteQuoteTable = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (quoteId: number) => deleteQuoteTable(rfxId, quoteId, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
			// A deleted quote table takes every one of its columns with it — any
			// summary table referencing them by name is now dangling.
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to delete table. Please try again.", "Error");
		},
	});
};

export const useCopyQuoteTable = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (quoteId: number) => copyQuoteTable(rfxId, quoteId, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to copy table. Please try again.", "Error");
		},
	});
};

export const useReorderQuoteTables = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();
	const queryKey = [QUOTE_TABLES_KEY, resourceType, rfxId];

	return useMutation({
		mutationFn: (quoteIds: number[]) =>
			reorderQuoteTables(rfxId, { quote_ids: quoteIds }, resourceType),
		onMutate: async (quoteIds: number[]) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<RfxQuoteTable[]>(queryKey);
			if (previous) {
				const idToTable = new Map(previous.map((t) => [t.id, t]));
				const reordered = quoteIds
					.map((id) => idToTable.get(id))
					.filter(Boolean) as RfxQuoteTable[];
				queryClient.setQueryData(queryKey, reordered);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
			showToast("Failed to reorder tables. Please try again.", "Error");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
};

// --- Row mutations ---

export const useCreateQuoteRow = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { payload: CreateQuoteRowPayload; quoteId: number; }) =>
			createQuoteRow(rfxId, params.quoteId, params.payload, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to add row. Please try again.", "Error");
		},
	});
};

export const useBatchCreateQuoteRows = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { quoteId: number; rows: BatchCreateRowPayload[] }) =>
			batchCreateQuoteRows(rfxId, params.quoteId, params.rows, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to add rows. Please try again.", "Error");
		},
	});
};

export const useUpdateQuoteRow = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: {
			payload: UpdateQuoteRowPayload;
			quoteId: number;
			rowId: number;
		}) => updateQuoteRow(rfxId, params.quoteId, params.rowId, params.payload, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to update row. Please try again.", "Error");
		},
	});
};

export const useDeleteQuoteRow = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { quoteId: number; rowId: number }) =>
			deleteQuoteRow(rfxId, params.quoteId, params.rowId, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to delete row. Please try again.", "Error");
		},
	});
};

export const useBatchDeleteQuoteRows = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { quoteId: number; rowIds: number[] }) =>
			batchDeleteQuoteRows(rfxId, params.quoteId, params.rowIds, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to delete rows. Please try again.", "Error");
		},
	});
};

export const useReorderQuoteRows = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();
	const queryKey = [QUOTE_TABLES_KEY, resourceType, rfxId];

	return useMutation({
		mutationFn: (params: { quoteId: number; rowIds: number[] }) =>
			reorderQuoteRows(rfxId, params.quoteId, { row_ids: params.rowIds }, resourceType),
		onMutate: async (params: { quoteId: number; rowIds: number[] }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<RfxQuoteTable[]>(queryKey);
			if (previous) {
				const updated = previous.map((table) => {
					if (table.id !== params.quoteId) {
						return table;
					}
					const idToRow = new Map(table.rows.map((r) => [r.id, r]));
					const reorderedRows = params.rowIds
						.map((id) => idToRow.get(id))
						.filter(Boolean) as RfxQuoteItem[];
					return { ...table, rows: reorderedRows };
				});
				queryClient.setQueryData(queryKey, updated);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
			showToast("Failed to reorder rows. Please try again.", "Error");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
};

export const useBatchUpdateQuoteRows = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();
	const queryKey = [QUOTE_TABLES_KEY, resourceType, rfxId];

	return useMutation({
		mutationFn: (params: { quoteId: number; rows: BatchUpdateRowPayload[] }) =>
			batchUpdateQuoteRows(rfxId, params.quoteId, params.rows, resourceType),
		onMutate: async (params: { quoteId: number; rows: BatchUpdateRowPayload[] }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<RfxQuoteTable[]>(queryKey);
			if (previous) {
				const rowUpdates = new Map(params.rows.map((r) => [r.id, r]));
				const updated = previous.map((table) => {
					if (table.id !== params.quoteId) {
						return table;
					}
					return {
						...table,
						rows: table.rows.map((row) => {
							const patch = rowUpdates.get(row.id);
							if (!patch) {
								return row;
							}
							return {
								...row,
								...(patch.name !== undefined && { name: patch.name }),
								...(patch.unit_id !== undefined && { unitId: patch.unit_id }),
							};
						}),
					};
				});
				queryClient.setQueryData(queryKey, updated);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
			showToast("Failed to save row changes. Please try again.", "Error");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
};

// --- Column mutations ---

export const useCreateQuoteColumn = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: {
			columnType: RfxQuoteColumnType;
			name: string;
			quoteId: number;
		}) =>
			createQuoteColumn(rfxId, params.quoteId, {
				column_type: params.columnType,
				name: params.name,
			}, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
			// A new column can repair a summary table's dangling reference
			// if it's added back under the same name.
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to add column. Please try again.", "Error");
		},
	});
};

export const useUpdateQuoteColumn = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: {
			columnId: number;
			payload: UpdateColumnPayload;
			quoteId: number;
		}) => updateQuoteColumn(rfxId, params.quoteId, params.columnId, params.payload, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
			// Summary tables reference value columns by NAME with no FK
			// (RfxSummaryTableService::calculateTotals) — a rename can both
			// break and repair a reference.
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to update column. Please try again.", "Error");
		},
	});
};

export const useDeleteQuoteColumn = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { columnId: number; quoteId: number; }) =>
			deleteQuoteColumn(rfxId, params.quoteId, params.columnId, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
			// A deleted column is exactly the dangling-reference case a
			// summary table can be left holding.
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to delete column. Please try again.", "Error");
		},
	});
};

export const useReorderQuoteColumns = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { columnIds: number[]; quoteId: number; }) =>
			reorderQuoteColumns(rfxId, params.quoteId, { column_ids: params.columnIds }, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to reorder columns. Please try again.", "Error");
		},
	});
};

// --- Existing Value Columns (cross-table) ---

const EXISTING_VALUE_COLUMNS_KEY = "procurer-existing-value-columns";

export const useExistingValueColumns = (rfxId: number | null, resourceType: QuoteResourceType = "rfx") => {
	return useQuery({
		queryKey: [EXISTING_VALUE_COLUMNS_KEY, resourceType, rfxId],
		queryFn: () => getExistingValueColumns(rfxId!, resourceType),
		enabled: !!rfxId,
	});
};

// --- Summary Tables ---

const SUMMARY_TABLES_KEY = "procurer-summary-tables";
const SUMMARY_TABLE_DATA_KEY = "procurer-summary-table-data";

/**
 * The full per-table data query key. Registered here and read back via
 * `summaryTableIdFromDataKey` below — kept next to each other so a change to
 * the key's shape is a change to both, instead of two literals (one in the
 * query, one in a mutation's cache filter) that can silently drift apart.
 */
const summaryTableDataKey = (
	resourceType: QuoteResourceType,
	rfxId: number | null,
	summaryTableId: number | null,
	inviteId?: number
) => [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId, summaryTableId, inviteId] as const;

/** The summaryTableId segment of a key built by `summaryTableDataKey`. */
const summaryTableIdFromDataKey = (
	queryKey: readonly unknown[]
): unknown => queryKey[3];

export const useSummaryTables = (rfxId: number | null, resourceType: QuoteResourceType = "rfx") => {
	return useQuery({
		queryKey: [SUMMARY_TABLES_KEY, resourceType, rfxId],
		queryFn: () => getSummaryTables(rfxId!, resourceType),
		enabled: !!rfxId,
	});
};

/**
 * A single summary table's computed cells/totals/validity. Kept as
 * its own query, separate from `useSummaryTables` (the config list), because
 * this is the endpoint that reports dangling column references — it needs to
 * refetch whenever a quote column or the summary table's own config changes,
 * not just on mount. See the invalidation calls on the column and
 * summary-table CRUD mutations below.
 */
export const useSummaryTableData = (
	rfxId: number | null,
	summaryTableId: number | null,
	inviteId?: number,
	resourceType: QuoteResourceType = "rfx"
) => {
	return useQuery({
		queryKey: summaryTableDataKey(resourceType, rfxId, summaryTableId, inviteId),
		queryFn: () => getSummaryTableData(rfxId!, summaryTableId!, inviteId, resourceType),
		enabled: !!rfxId && !!summaryTableId,
	});
};

export const useCreateSummaryTable = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: CreateSummaryTablePayload) =>
			createSummaryTable(rfxId, payload, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLES_KEY, resourceType, rfxId],
			});
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to create summary table. Please try again.", "Error");
		},
	});
};

export const useUpdateSummaryTable = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { payload: UpdateSummaryTablePayload; summaryTableId: number; }) =>
			updateSummaryTable(rfxId, params.summaryTableId, params.payload, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLES_KEY, resourceType, rfxId],
			});
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to update summary table. Please try again.", "Error");
		},
	});
};

export const useDeleteSummaryTable = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (summaryTableId: number) =>
			deleteSummaryTable(rfxId, summaryTableId, resourceType),
		onSuccess: (_data, summaryTableId) => {
			// Drop the deleted table from the list's cache synchronously, rather
			// than just invalidating and waiting on a refetch. The list refetch
			// is async; a plain `invalidateQueries` here leaves the `SummaryTable`
			// instance for this id mounted (with its own `useSummaryTableData`
			// observer still live) for at least one more render. If anything
			// re-renders `ProcurerQuoteTab` in that window (e.g. the delete
			// modal's own onSuccess clearing its pending-id state), that mounted
			// observer sees no cached data for its key and issues a fresh
			// `GET /summary-tables/{id}/data` — which now 404s, since the row is
			// already gone server-side. Confirmed via network trace: the 404
			// request lands *after* the list refetch already excludes it,
			// meaning the belated per-table fetch — not a stale request — is the
			// culprit. Removing the entry from the list synchronously takes the
			// `<SummaryTable>` instance (and its observer) out with it before any
			// such re-render can fire.
			queryClient.setQueryData<SummaryTableConfig[]>(
				[SUMMARY_TABLES_KEY, resourceType, rfxId],
				(prev) => prev?.filter((table) => table.id !== summaryTableId)
			);
			// Reconcile with the server in the background.
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLES_KEY, resourceType, rfxId],
			});
			// Every *other* summary table's data still needs a refetch —
			// deleting this one can repair or break their value-column
			// references, since those match by column name, not id. The
			// predicate excludes the deleted table's own key: `setQueryData`
			// above only takes effect on React's *next* render (unmounting the
			// `SummaryTable` instance and its observer with it) — this
			// `invalidateQueries` call runs synchronously, in the same tick, so
			// without the exclusion it would still find that observer "active"
			// and refetch it immediately, before the unmount has had a chance
			// to happen. Excluding it here closes that race outright, rather
			// than relying on unmount timing.
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId],
				predicate: (query) =>
					summaryTableIdFromDataKey(query.queryKey) !== summaryTableId,
			});
		},
		onError: () => {
			showToast("Failed to delete summary table. Please try again.", "Error");
		},
	});
};

// --- Save as Template / Create from Template ---

export const useSaveQuoteAsTemplate = (rfxId: number, onSaved?: () => void, resourceType: QuoteResourceType = "rfx") => {
	return useMutation({
		mutationFn: (params: { name: string; quoteId: number; }) =>
			saveQuoteAsTemplate(rfxId, params.quoteId, params.name, resourceType),
		onSuccess: () => {
			showToast("Template saved successfully.", "Success", true);
			onSaved?.();
		},
		onError: () => {
			showToast("Failed to save template. Please try again.", "Error");
		},
	});
};

export const useCreateQuoteTableFromTemplate = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (templateId: number) =>
			createQuoteTableFromTemplate(rfxId, templateId, resourceType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUOTE_TABLES_KEY, resourceType, rfxId],
			});
		},
		onError: () => {
			showToast("Failed to create table from template. Please try again.", "Error");
		},
	});
};

// --- Sync Layout (single-call table update) ---

export const useSyncQuoteTableLayout = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();
	const queryKey = [QUOTE_TABLES_KEY, resourceType, rfxId];

	return useMutation({
		mutationFn: (params: { payload: SyncLayoutPayload; quoteId: number; }) =>
			syncQuoteTableLayout(rfxId, params.quoteId, params.payload, resourceType),
		onMutate: async (params: { payload: SyncLayoutPayload; quoteId: number; }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<RfxQuoteTable[]>(queryKey);
			if (previous) {
				const updated = previous.map((table) => {
					if (table.id !== params.quoteId) {
						return table;
					}
					return {
						...table,
						...(params.payload.name !== undefined && { name: params.payload.name }),
						...(params.payload.description !== undefined && { description: params.payload.description }),
					};
				});
				queryClient.setQueryData(queryKey, updated);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
			showToast("Failed to update table layout. Please try again.", "Error");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
			// Edit Layout can rename/retype/add/remove value columns on an existing
			// table in bulk — any summary table referencing them by name is now stale.
			queryClient.invalidateQueries({
				queryKey: [SUMMARY_TABLE_DATA_KEY, resourceType, rfxId],
			});
		},
	});
};

// --- Cell batch update (procurer-editable columns) ---

export const useBatchUpdateCells = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	const queryClient = useQueryClient();
	const queryKey = [QUOTE_TABLES_KEY, resourceType, rfxId];

	return useMutation({
		mutationFn: (params: { cells: CellUpdatePayload[]; quoteId: number; }) =>
			batchUpdateCells(rfxId, params.quoteId, params.cells, resourceType),
		onMutate: async (params: { cells: CellUpdatePayload[]; quoteId: number; }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<RfxQuoteTable[]>(queryKey);
			if (previous) {
				const updated = previous.map((table) => {
					if (table.id !== params.quoteId) {
						return table;
					}
					return {
						...table,
						rows: table.rows.map((row) => {
							const cellUpdates = params.cells.filter((c) => c.row_id === row.id);
							if (cellUpdates.length === 0) {
								return row;
							}
							const existingCells = [...(row.cells || [])];
							for (const update of cellUpdates) {
								const idx = existingCells.findIndex(
									(c) => c.rfxQuoteColumnId === update.column_id
								);
								if (idx >= 0) {
									existingCells[idx] = { ...existingCells[idx], value: update.value };
								} else {
									existingCells.push({
										id: 0,
										rfxQuoteRowId: row.id,
										rfxQuoteColumnId: update.column_id,
										rfxInviteId: null,
										value: update.value,
										numericValue: null,
										revision: 0,
										is_submitted: false,
									});
								}
							}
							return { ...row, cells: existingCells };
						}),
					};
				});
				queryClient.setQueryData(queryKey, updated);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
			showToast("Failed to save cell values. Please try again.", "Error");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
};

// --- Quote Settings ---

export const useUpdateQuoteSettings = (rfxId: number, resourceType: QuoteResourceType = "rfx") => {
	return useMutation({
		mutationFn: (payload: QuoteSettings) =>
			updateQuoteSettings(rfxId, payload, resourceType),
		onError: () => {
			showToast("Failed to save quote settings. Please try again.", "Error");
		},
	});
};

export const useUpdateTabStatus = (rfxId: number) => {
	return useMutation({
		mutationFn: (payload: TabStatusPayload) =>
			updateTabStatus(rfxId, payload),
		onError: () => {
			showToast("Failed to update tab status.", "Error");
		},
	});
};
